import { useAppSelector } from "@app/hooks";
import TransformedImage from "@components/TransformedImage";
import { EmeraldIcon } from "@emerald-react/icon";
import {
  getImageClasses,
  transformImageUrl,
} from "@shared/utils/imageUrlTransforms";
import { getIconData } from "@shared/utils/map";
import type { Property } from "@survey-types/survey.property.types";
import {
  selectCurrentSurvey,
  selectPaperSize,
  selectPropertyMedia,
} from "@survey/surveySelectors";
import classNames from "classnames";

interface PropertyFeatureImageProps {
  propertyId: string;
  size: "thumbnail" | "standard" | "fullsize" | "web";
  order: number;
  isShowIcon?: boolean;
  asBackground?: boolean;
  comparison?: boolean; // New prop to indicate if it's used in comparison context
}

// Property icon/number overlay component - moved outside render
const PropertyIcon = ({
  property,
  order,
}: {
  property?: Property;
  order: number;
}) => {
  if (!property) return null;

  const iconData = getIconData(property);

  if (property.isCurrentLocation) {
    return (
      <div className="w-6 h-6 rounded-full flex bg-white items-center justify-center">
        <EmeraldIcon
          icon="stars"
          type="filled"
          variant="icon"
          color={iconData.iconColor}
          className="text-2xl!"
        />
      </div>
    );
  }

  return (
    <div
      className="w-6 h-6 rounded-full flex items-center justify-center text-white text-sm"
      style={{ backgroundColor: iconData.iconColor }}
    >
      {order}
    </div>
  );
};

/**
 * PropertyFeatureImage Component
 * Displays property feature image with optional property number overlay or star icon
 * Uses TransformedImage component for EDP URL transformation
 * React 19 standards with arrow functions
 */
const PropertyFeatureImage = ({
  propertyId,
  size,
  order,
  isShowIcon = false,
  asBackground = false,
  comparison = false,
}: PropertyFeatureImageProps) => {
  // Use store-first architecture with selectors
  const survey = useAppSelector(selectCurrentSurvey);
  const paperSize = useAppSelector(selectPaperSize);
  const propertyMedia = useAppSelector(selectPropertyMedia(propertyId));

  // Find the property from survey
  const property =
    survey?.properties?.find(
      (item: Property) => item.propertyId === propertyId,
    ) || ({} as Property);

  // Process image URL and class based on size and media state
  const activeImg = propertyMedia?.images?.find(
    (item) => !item.isHidden && item.cloudfrontUrl,
  );

  let url = "";

  // Only set image URL if we have an active (non-hidden) image
  if (activeImg) {
    const baseUrl = activeImg.cloudfrontUrl || "";
    const origin = activeImg.origin || "";

    // Use utility function for URL transformation
    url = transformImageUrl(baseUrl, origin, size);
  }

  // Use utility function for responsive classes
  const imgClass = getImageClasses(size, paperSize === "Portrait");

  const imageData = {
    url,
    imgClass,
  };

  return (
    <div
      className={classNames(
        "grid grid-cols-1 grid-rows-1",
        !imageData.url ? imageData.imgClass : undefined,
      )}
    >
      {/* Main image content */}
      <div
        className={classNames("col-start-1 row-start-1", {
          "mb-2": comparison && imageData.url, // Add margin-bottom if in comparison and image exists
        })}
      >
        <TransformedImage
          imageUrl={`${imageData.url}`}
          alt={property.name || `Property ${order}`}
          className={classNames(
            "bg-gray-50 justify-center bg-cover",
            imageData.imgClass,
          )}
          asBackground={asBackground}
          showFallback={!imageData.url}
          imgClass="max-h-full"
        >
          {!imageData.url && (
            <div className="w-full h-23 flex items-center justify-center text-gray-400">
              <EmeraldIcon
                icon="business"
                type="filled"
                variant="icon"
                className={classNames({
                  "text-8xl!": !comparison,
                  "text-5xl!": comparison,
                })}
              />
            </div>
          )}
        </TransformedImage>
      </div>

      {/* Icon overlay positioned at top-left with gap using grid positioning */}
      {isShowIcon && (
        <div className="col-start-1 row-start-1 z-10 justify-self-start self-start m-1">
          <PropertyIcon property={property} order={order} />
        </div>
      )}
    </div>
  );
};

export default PropertyFeatureImage;
